import type { Reactive } from 'vue'
import type { ComponentProps } from 'vue-component-type-helpers'
import { createSharedComposable } from '@vueuse/core'

/**
 * Programmatic dialog system.
 *
 * Vendored into the main app (was previously only in the `layers/ui` git
 * submodule, which is not reliably present at build time — its composables
 * weren't auto-imported under Nuxt 4's appDir, causing `useDialog is not
 * defined` at runtime). Keeping it here guarantees it auto-imports normally.
 *
 * The provider is set up by `app/plugins/modal.ts`; the rendered output is
 * handled by the `C/DialogWrapper` component used by each dialog.
 */
export interface ModalState {
  component: Component | string
  props: ComponentProps<Component>
}

export const dialogInjectionKey: InjectionKey<Reactive<Map<symbol, ModalState>>> = Symbol('trakle.modal')

export const useDialog = createSharedComposable(() => {
  const state = inject(dialogInjectionKey)
  if (!state) throw new Error('useDialog() is called without provider')

  return function open<T extends Component, C = Parameters<ComponentProps<T>['onClose']>[0]>(component: T, props?: ComponentProps<T>): Promise<C> {
    component = markRaw(component)
    const symbol = Symbol('Dialog')
    const { promise, resolve } = Promise.withResolvers<C>()
    state.set(symbol, { component, props: { ...props, onClose: resolve } })
    promise.finally(() => state.delete(symbol))
    return promise
  }
})
